import { memo, useCallback, useEffect, useRef, useState } from "react";
import Camera from "@zenith/assets/icons/Camera";
import CheckCircle from "@zenith/assets/icons/CheckCircle";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import Eye from "@zenith/assets/icons/Eye";
import Home from "@zenith/assets/icons/Home";
import Info from "@zenith/assets/icons/Info";
import Smile from "@zenith/assets/icons/Smile";
import { ButtonComponent, SpinnerComponent } from "@zenith/components";
import {
  ImageSelfieRequestDTO,
  ProfileUpdateApiService,
  SelfieStatusResponseDTO,
  UsersApiService,
} from "@zenith/services/user";

const useSmileIdWebComponent = () => {
  const [isComponentLoaded, setIsComponentLoaded] = useState(false);

  useEffect(() => {
    const markReady = () => {
      if (
        customElements.get("selfie-capture-screens") ||
        customElements.get("smart-camera-web")
      ) {
        setIsComponentLoaded(true);
        return;
      }
      Promise.race([
        customElements.whenDefined("smart-camera-web"),
        customElements.whenDefined("selfie-capture-screens"),
      ]).then(() => setIsComponentLoaded(true));
    };

    const existingScript = document.getElementById("smile-id-script-v11");
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = "smile-id-script-v11";
      script.type = "module";
      script.src = "https://cdn.smileidentity.com/js/v11/smart-camera-web.js";
      script.onload = markReady;
      script.onerror = () => console.error("Failed to load Smile ID script");
      document.head.appendChild(script);
    } else {
      markReady();
    }
  }, []);

  return { isComponentLoaded };
};

export interface ProfilePhotoVerificationProps {
  onPhotoVerified: (jobId: string) => void;
  onGoBack: () => void;
}

const ProfilePhotoVerificationComponent = memo(
  function ProfilePhotoVerificationComponent({
    onPhotoVerified,
    onGoBack,
  }: ProfilePhotoVerificationProps) {
    const [showSmileIdCamera, setShowSmileIdCamera] = useState(false);
    const [capturedImages, setCapturedImages] = useState<
      ImageSelfieRequestDTO[]
    >([]);
    const [jobId, setJobId] = useState<string>("");
    const [selfieStatus, setSelfieStatus] = useState<string | undefined>(
      undefined
    );
    const [isPhotoSubmitted, setIsPhotoSubmitted] = useState(false);
    const [verifyingSelfie, setVerifyingSelfie] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
      undefined
    );
    const [secondErrorMessage, setSecondErrorMessage] = useState<
      string | undefined
    >(undefined);
    const selfiesSubmittedRef = useRef(false);
    const smileIdRef = useRef<HTMLElement | null>(null);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
      null
    );

    const { isComponentLoaded } = useSmileIdWebComponent();

    useEffect(() => {
      if (!verifyingSelfie || !jobId) return;

      const clearPoll = () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };

      const poll = async () => {
        try {
          const response = await UsersApiService.getSelfieStatus(jobId);
          const status = response.data?.status;
          const message = response.data?.message;

          const isError =
            status === SelfieStatusResponseDTO.status.ERROR_IN_VERIFICATION ||
            message ===
              SelfieStatusResponseDTO.message.GENERAL_ERROR_IN_VERIFICATION ||
            message === SelfieStatusResponseDTO.message.NO_FACE_FOUND ||
            message === SelfieStatusResponseDTO.message.LOW_IMAGE_QUALITY;

          if (isError) {
            clearPoll();
            setSelfieStatus(
              SelfieStatusResponseDTO.status.ERROR_IN_VERIFICATION
            );
            setVerifyingSelfie(false);
            setErrorMessage("There was a problem with photo verification");
            setSecondErrorMessage("Please try again");
            return;
          }

          if (status === SelfieStatusResponseDTO.status.VERIFIED_SUCCESSFULLY) {
            clearPoll();
            setSelfieStatus(status);
            setTimeout(() => setVerifyingSelfie(false), 500);
          }
        } catch {
          clearPoll();
          setSelfieStatus("ERROR_IN_VERIFICATION");
          setVerifyingSelfie(false);
          setErrorMessage("There was a problem verifying your selfie");
          setSecondErrorMessage("Please try again with a clearer photo");
        }
      };

      poll();
      pollingIntervalRef.current = setInterval(poll, 5000);

      return clearPoll;
    }, [verifyingSelfie, jobId]);

    const cleanBase64 = (base64String: string): string => {
      if (base64String.startsWith("data:image/")) {
        return base64String.split(",")[1];
      }
      return base64String;
    };

    const convertSmileIdToApiFormat = useCallback(
      (smileIdImages: { image: string }[]): ImageSelfieRequestDTO[] => {
        return smileIdImages.map((img, index) => ({
          imageContent: cleanBase64(img.image),
          imageType:
            index === smileIdImages.length - 1
              ? ImageSelfieRequestDTO.imageType.SELFIE_BASE64
              : ImageSelfieRequestDTO.imageType.LIVENESS_BASE64,
          mimeExt: "jpeg",
        }));
      },
      []
    );

    // Inject CSS into SmileID's nested shadow DOM
    useEffect(() => {
      if (!showSmileIdCamera) return;

      const el = smileIdRef.current;
      if (!el) return;

      const outerShadow = (
        el as HTMLElement & { shadowRoot: ShadowRoot | null }
      ).shadowRoot;

      const STYLE_CONTENT = `
        #selfie-capture-instruction-screen { padding-block: 0.75rem !important; }
        header svg { height: 40px !important; width: auto !important; }
        header { padding-block: 4px !important; }
        .instructions-wrapper { gap: 0.625rem !important; margin-block-start: 0.5rem !important; margin-block-end: 0.5rem !important; }
        .instructions svg { margin-inline-end: 0.75rem !important; }
        .flow > * + * { margin-top: 0.5rem !important; }
      `;

      let injectedSheet: CSSStyleSheet | null = null;
      let lastInnerShadow: ShadowRoot | null = null;

      const getInnerShadow = (): ShadowRoot | null => {
        const instructionEl =
          (el as HTMLElement).querySelector("selfie-capture-instructions") ??
          outerShadow?.querySelector("selfie-capture-instructions");
        if (!instructionEl) return null;
        return (
          (instructionEl as HTMLElement & { shadowRoot: ShadowRoot | null })
            .shadowRoot ?? null
        );
      };

      const overrideInlineFlowSpace = (shadow: ShadowRoot) => {
        shadow
          .querySelectorAll<HTMLElement>("section.flow")
          .forEach((flowEl) => {
            if (flowEl.style.getPropertyValue("--flow-space") !== "0.75rem") {
              flowEl.style.setProperty("--flow-space", "0.75rem");
            }
          });
      };

      const injectIntoInnerShadow = () => {
        const innerShadow = getInnerShadow();
        if (!innerShadow) return;

        if (innerShadow !== lastInnerShadow) {
          lastInnerShadow = innerShadow;
          try {
            injectedSheet = new CSSStyleSheet();
            injectedSheet.replaceSync(STYLE_CONTENT);
            innerShadow.adoptedStyleSheets = [
              ...innerShadow.adoptedStyleSheets,
              injectedSheet,
            ];
          } catch {
            const STYLE_ID = "zenz-smileid-profile-styles";
            if (!innerShadow.getElementById(STYLE_ID)) {
              const style = document.createElement("style");
              style.id = STYLE_ID;
              style.textContent = STYLE_CONTENT;
              innerShadow.appendChild(style);
            }
          }
        }

        overrideInlineFlowSpace(innerShadow);
      };

      injectIntoInnerShadow();

      const observer = new MutationObserver(injectIntoInnerShadow);
      observer.observe(el as HTMLElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });

      return () => {
        observer.disconnect();
        if (injectedSheet && lastInnerShadow) {
          lastInnerShadow.adoptedStyleSheets =
            lastInnerShadow.adoptedStyleSheets.filter(
              (s) => s !== injectedSheet
            );
        }
      };
    }, [showSmileIdCamera]);

    useEffect(() => {
      if (!isComponentLoaded || !showSmileIdCamera) return;

      const smileIdElement = smileIdRef.current;
      if (!smileIdElement) return;

      const handleSelfieCapture = (event: CustomEvent) => {
        if (event.detail && event.detail.images) {
          const converted = convertSmileIdToApiFormat(event.detail.images);
          setCapturedImages(converted);
          setShowSmileIdCamera(false);
        }
      };

      const handleSelfieCancel = () => setShowSmileIdCamera(false);
      const handleSelfieBack = () => setShowSmileIdCamera(false);

      smileIdElement.addEventListener(
        "selfie-capture-screens.publish",
        handleSelfieCapture as EventListener
      );
      smileIdElement.addEventListener(
        "selfie-capture-screens.cancelled",
        handleSelfieCancel as EventListener
      );
      smileIdElement.addEventListener(
        "selfie-capture-screens.back",
        handleSelfieBack as EventListener
      );

      return () => {
        smileIdElement.removeEventListener(
          "selfie-capture-screens.publish",
          handleSelfieCapture as EventListener
        );
        smileIdElement.removeEventListener(
          "selfie-capture-screens.cancelled",
          handleSelfieCancel as EventListener
        );
        smileIdElement.removeEventListener(
          "selfie-capture-screens.back",
          handleSelfieBack as EventListener
        );
      };
    }, [isComponentLoaded, showSmileIdCamera, convertSmileIdToApiFormat]);

    useEffect(() => {
      if (capturedImages.length === 0 || selfiesSubmittedRef.current) return;
      selfiesSubmittedRef.current = true;
      setIsUploading(true);

      ProfileUpdateApiService.submitProfileUpdateSelfie({
        images: capturedImages,
      })
        .then((response) => {
          const newJobId = response.data?.jobId ?? "";
          setJobId(newJobId);
          setIsPhotoSubmitted(true);
          setVerifyingSelfie(true);
          setIsUploading(false);
        })
        .catch(() => {
          setIsUploading(false);
          selfiesSubmittedRef.current = false;
          setErrorMessage("Failed to submit your selfie");
          setSecondErrorMessage("Please try again or contact support");
        });
    }, [capturedImages]);

    const handleTryAgain = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setVerifyingSelfie(false);
      setJobId("");
      setIsPhotoSubmitted(false);
      setSelfieStatus(undefined);
      setErrorMessage(undefined);
      setSecondErrorMessage(undefined);
      selfiesSubmittedRef.current = false;
      setCapturedImages([]);
    };

    const shouldShowErrorUI =
      selfieStatus === "ERROR_IN_VERIFICATION" || !!errorMessage;

    const isVerifiedSuccessfully =
      !verifyingSelfie &&
      selfieStatus === SelfieStatusResponseDTO.status.VERIFIED_SUCCESSFULLY;

    const renderInstructions = () => (
      <>
        <ul className="flex flex-col gap-y-8 text-base-lh-17 font-normal text-neutral-600">
          <li className="flex flex-row">
            <Camera className="mt-1" />
            <p className="ml-3 w-full">
              We need access to your camera so that we can take your photograph
              and proof-of-life images
            </p>
          </li>
          <li className="flex flex-row">
            <Smile className="mt-1" />
            <p className="ml-3 w-full">
              Please, face the camera and keep a straight face
            </p>
          </li>
          <li className="flex flex-row">
            <Home className="mt-1" />
            <p className="ml-3 w-full">
              Ensure you are in a well lit environment
            </p>
          </li>
          <li className="flex flex-row">
            <Eye className="mt-1" />
            <p className="ml-3 w-full">
              Remove contacts, glasses and other facial accessories before you
              start.
            </p>
          </li>
        </ul>
        <div className="my-12 flex w-full items-start rounded-1.25l bg-[#5656CA14] p-4">
          <p className="mt-0.5">
            <Info />
          </p>
          <p className="ml-3 text-sm font-normal text-neutral-900">
            Your photograph is safe and will only be used for identification
            purposes.
          </p>
        </div>
        <ButtonComponent
          id="continue-to-photo-verification"
          label={!isComponentLoaded ? "Loading..." : "Start Photo Verification"}
          onClick={() => setShowSmileIdCamera(true)}
          type="primary"
          size="large"
          fullWidth
          disabled={!isComponentLoaded || isUploading}
          isLoading={isUploading}
        />
      </>
    );

    return (
      <>
        {showSmileIdCamera && (
          <div className="fixed inset-0 z-[10000] overflow-y-auto overflow-x-hidden bg-black bg-opacity-75">
            <div className="flex min-h-full items-center justify-center py-4">
              <style>{`.smile-card::-webkit-scrollbar{display:none}`}</style>
              <div
                className="smile-card relative mx-auto max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white px-4"
                style={
                  {
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  } as React.CSSProperties
                }
              >
                <div className="absolute inset-x-0 top-0 z-10 h-16 bg-white" />
                <button
                  onClick={() => setShowSmileIdCamera(false)}
                  className="absolute top-4 right-4 z-20 rounded-full bg-white p-2 shadow-lg"
                >
                  <CloseCircle width={24} height={24} />
                </button>
                <selfie-capture-screens
                  ref={smileIdRef}
                  theme-color={"#DA291C"}
                  hide-back-to-host
                />
              </div>
            </div>
          </div>
        )}

        {isUploading || verifyingSelfie ? (
          <div className="flex min-h-[70vh] flex-col items-center justify-center">
            <h1 className="mb-24 text-4.5xl font-medium text-neutral-900">
              Photo Verification
            </h1>
            <SpinnerComponent size={64} type={"primary"} showIcon={false} />
            <p className="mt-6 text-base font-normal text-neutral-600">
              Your photograph is being verified.
            </p>
            <p className="text-sm font-normal text-[#9E9E9E]">
              This should not take too long.
            </p>
          </div>
        ) : shouldShowErrorUI ? (
          <div className="flex min-h-[70vh] flex-col justify-center">
            <h1 className="mb-10 text-4.5xl font-medium text-neutral-900">
              Photo Verification
            </h1>
            <div className="flex items-center">
              <CloseCircle stroke="#DA291C" width={32} height={32} />
              <h4 className="ml-2 text-2xl">Photo Verification Issue</h4>
            </div>
            <p className="ml-10 text-sm text-neutral-600">{errorMessage}</p>
            {secondErrorMessage && (
              <p className="mb-20 ml-10 text-sm text-neutral-400">
                {secondErrorMessage}
              </p>
            )}
            <div className="flex gap-4">
              <ButtonComponent
                id="back-button"
                label="Back"
                onClick={onGoBack}
                type="secondary"
                size="large"
                fullWidth
              />
              <ButtonComponent
                id="try-again-button"
                label="Try Again"
                onClick={handleTryAgain}
                type="primary"
                size="large"
                fullWidth
              />
            </div>
          </div>
        ) : !isPhotoSubmitted ? (
          <div className="flex min-h-[80vh] flex-col justify-center">
            <h1 className="mb-10 whitespace-nowrap text-4.5xl font-medium text-neutral-900">
              Facial Image Authentication.
            </h1>
            {renderInstructions()}
          </div>
        ) : isVerifiedSuccessfully ? (
          <div className="flex min-h-[70vh] flex-col justify-center">
            <h1 className="mb-10 text-4.5xl font-medium text-neutral-900">
              Photo Verification
            </h1>
            <div className="flex items-center pt-4">
              <CheckCircle width={32} height={32} />
              <h4 className="ml-2 text-2xl">Photo Successfully Verified</h4>
            </div>
            <p className="mb-20 ml-10 text-sm text-neutral-600">
              Press &quot;Continue&quot; to proceed.
            </p>
            <div className="flex gap-4">
              <ButtonComponent
                id="back-button"
                label="Back"
                onClick={onGoBack}
                type="secondary"
                size="large"
                fullWidth
              />
              <ButtonComponent
                id="continue-button"
                label="Continue"
                onClick={() => onPhotoVerified(jobId)}
                type="primary"
                size="large"
                fullWidth
              />
            </div>
          </div>
        ) : null}
      </>
    );
  }
);

export default ProfilePhotoVerificationComponent;
